import React from "react";
import { useSelector } from "react-redux";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import { TextField, Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/styles";
import translations from "app/libs/i18n/translations.json";
import { useState } from "react";
import { useEffect } from "react";
import { useIntl } from "react-intl";

const filter = createFilterOptions();

// Language options are listed by tier: Belgian national languages first (in
// this exact order), then widely spoken languages, then the rest alphabetically.
const PRIORITY_LANGUAGES = ["french", "dutch", "english"];
const COMMON_LANGUAGES = [
  "german",
  "spanish",
  "italian",
  "portuguese",
  "arabic",
  "turkish",
  "polish",
  "romanian",
  "russian",
  "chinese",
];

const languageRank = (id) => {
  const priorityIndex = PRIORITY_LANGUAGES.indexOf(id);
  if (priorityIndex !== -1) return priorityIndex;
  return COMMON_LANGUAGES.includes(id) ? PRIORITY_LANGUAGES.length : PRIORITY_LANGUAGES.length + 1;
};

const candidateInputSx = {
  width: "100%",
  borderRadius: "12px",
  border: "1px solid hsla(160, 10%, 90%, 0.6)",
  bgcolor: "hsla(160, 10%, 96%, 0.3)",
  transition: "border-color 0.15s",
  "&:focus-within": { borderColor: "hsla(153, 68%, 25%, 0.4)" },
  "& .MuiAutocomplete-root": { width: "100%" },
  "& .MuiOutlinedInput-root": {
    height: "44px",
    padding: "0 12px !important",
    fontSize: "13px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 93%, 10%)",
    bgcolor: "transparent",
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiInputLabel-root": { display: "none" },
  "& .MuiInputBase-input::placeholder": {
    fontSize: "13px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 10%, 45%)",
    opacity: 0.8,
  },
  "& .MuiAutocomplete-endAdornment": { display: "none" },
};

// Dropdown popup styled to match the CV-library region dropdown (rounded card,
// pill-shaped options, muted-to-teal hover).
const dropdownPaperSx = {
  mt: "8px",
  borderRadius: "12px",
  border: "1px solid hsl(160, 10%, 90%)",
  boxShadow: "0 4px 12px hsla(178, 93%, 10%, 0.08)",
  "& .MuiAutocomplete-listbox": {
    p: "8px",
  },
  "& .MuiAutocomplete-option": {
    borderRadius: "10px",
    minHeight: "auto",
    px: "12px !important",
    py: "8px !important",
    fontSize: "12px",
    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
    color: "hsl(178, 10%, 45%)",
    "&.Mui-focused, &:hover": {
      bgcolor: "hsla(160, 10%, 96%, 0.7)",
      color: "hsl(178, 93%, 10%)",
    },
  },
  "& .MuiAutocomplete-groupLabel": {
    fontSize: "11px",
    fontFamily: "Segoe UI Bold, sans-serif, system-ui",
    color: "hsl(178, 10%, 45%)",
    lineHeight: "28px",
    bgcolor: "transparent",
    px: "12px",
  },
};

const SkillSelect = ({
  selectedValues,
  category,
  label,
  disabled,
  onChange,
  allowAddingCustomSkills = false,
  candidateStyle = false,
}) => {
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const theme = useTheme();
  const intl = useIntl();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const localeTranslations = translations[i18nLocale] || (i18nLocale === "fr-fr" ? translations.fr : {});

  const optionsList = Object.entries(localeTranslations).filter((elem) =>
    elem[0].includes(`skills_list.${category}`)
  );

  const [options, setOptions] = useState([]);

  useEffect(() => {
    if (optionsList?.length || selectedValues?.length) {
      let baseOptions = optionsList.map((option) => {
        const splited = option[0].split(".");
        return { id: splited[splited.length - 1], label: option[1] };
      });

      let customOptions =
        currentRecruiter?.company?.skills
          ?.filter((s) => s.category == category)
          .map((s) => {
            return {
              id: s.name,
              label: s.name,
              company_id: currentRecruiter?.company_id,
            };
          }) || [];

      let createdOptions = currentRecruiter
        ? selectedValues
          .filter(
            (v) =>
              !baseOptions.map((option) => option.id).includes(v) &&
              !customOptions.map((option) => option.id).includes(v)
          )
          .map((v) => {
            return {
              id: v,
              label: v,
              company_id: currentRecruiter?.company_id,
            };
          })
        : [];
      setOptions(
        [...customOptions, ...createdOptions, ...baseOptions].sort((a, b) => {
          if (category === "lang") {
            const rankDiff = languageRank(a.id) - languageRank(b.id);
            if (rankDiff !== 0) return rankDiff;
          }
          let aLabel = a.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          let bLabel = b.label.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          if (aLabel < bLabel) {
            return -1;
          } else if (aLabel > bLabel) {
            return 1;
          }
          return 0;
        })
      );
    }
  }, [optionsList?.length, selectedValues?.length]);

  return (
    <Box sx={candidateStyle ? candidateInputSx : {}}>
      <Autocomplete
        multiple
        disableClearable
        disabled={disabled}
        options={options.filter((option) => !selectedValues.includes(option.id))}
        getOptionLabel={(option) => option.label}
        value={options.filter((option) => selectedValues.includes(option.id))}
        onChange={(event, newValue) => onChange(category, newValue)}
        componentsProps={candidateStyle ? { paper: { sx: dropdownPaperSx } } : undefined}
        renderInput={(params) => (
          <TextField
            {...params}
            label={candidateStyle ? undefined : label}
            placeholder={candidateStyle ? label : intl.formatMessage({ id: "select" })}
            color={currentRecruiter ? "secondary" : "primary"}
            sx={
              candidateStyle
                ? undefined
                : {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "#fff",
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "hsl(153, 68%, 25%)",
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "hsl(153, 68%, 25%)",
                    },
                  }
            }
            onKeyDown={(e) => {
              if (e.key === "Backspace") {
                e.stopPropagation();
              }
            }}
          />
        )}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const { inputValue } = params;
          const isExisting = options.some((option) => inputValue === option.id);
          if (inputValue !== "" && !isExisting && currentRecruiter && allowAddingCustomSkills) {
            filtered.push({
              id: inputValue,
              label: `${intl.formatMessage({ id: "add" })} "${inputValue}"`,
              company_id: currentRecruiter.company_id,
            });
          }

          return filtered;
        }}
        renderTags={() => {}}
        style={candidateStyle ? { width: "100%" } : { height: "64px", width: isDesktop ? "440px" : "100%", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}
      />
    </Box>
  );
};

export default SkillSelect;
